
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import Textarea from '../../../components/base/Textarea';

export default function ApplyFormSection() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    platforms: [] as string[],
    contentField: '',
    socialLinks: '',
    followers: '',
    cooperationMode: '',
    telegram: '',
    whatsapp: '',
    phone: '',
    email: '',
    message: '',
    privacyAccepted: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const platformOptions = [
    'TikTok', 'Instagram', 'YouTube', '小红书', 'B站', 'Twitter', 'Facebook', 'Snapchat'
  ];

  const contentFields = i18n.language === 'zh' ? [
    '美妆时尚', '科技数码', '生活方式', '健身运动', '美食料理', '旅行摄影', 
    '教育知识', '娱乐搞笑', '音乐舞蹈', '游戏电竞', '母婴亲子', '宠物动物'
  ] : [
    'Beauty & Fashion', 'Tech & Digital', 'Lifestyle', 'Fitness & Sports', 'Food & Cooking', 'Travel & Photography',
    'Education & Knowledge', 'Entertainment & Comedy', 'Music & Dance', 'Gaming & Esports', 'Parenting & Kids', 'Pets & Animals'
  ];

  const cooperationModes = i18n.language === 'zh' ? [
    '产品测评', '联盟营销', '品牌赞助', '混合模式'
  ] : [
    'Product Review', 'Affiliate Marketing', 'Brand Sponsorship', 'Mixed Mode'
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 清除联系方式错误提示
    if (['telegram', 'whatsapp', 'phone', 'email'].includes(field)) {
      setContactError('');
    }
  };

  const handlePlatformChange = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const validateContactInfo = () => {
    const { telegram, whatsapp, phone, email } = formData;
    if (!telegram && !whatsapp && !phone && !email) {
      const errorMsg = i18n.language === 'zh' 
        ? '请至少填写一种联系方式（Telegram、微信、电话或邮箱）' 
        : 'Please provide at least one contact method (Telegram, WeChat, Phone, or Email)';
      setContactError(errorMsg);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacyAccepted) {
      alert('请先同意隐私政策');
      return;
    }

    if (!validateContactInfo()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // 准备提交数据，确保数据格式正确且不被识别为垃圾信息
      const submitData: Record<string, string> = {};
      
      // 基本信息 - 添加更详细的标识
      if (formData.name.trim()) {
        submitData['applicant_name'] = formData.name.trim();
      }
      if (formData.country.trim()) {
        submitData['applicant_country'] = formData.country.trim();
      }
      if (formData.contentField) {
        submitData['content_category'] = formData.contentField;
      }
      
      // 平台信息 - 使用更清晰的字段名
      if (formData.platforms.length > 0) {
        submitData['social_platforms'] = formData.platforms.join(', ');
      }
      if (formData.socialLinks.trim()) {
        submitData['profile_links'] = formData.socialLinks.trim();
      }
      if (formData.followers.trim()) {
        submitData['follower_count'] = formData.followers.trim();
      }
      
      // 合作偏好
      if (formData.cooperationMode) {
        submitData['preferred_cooperation'] = formData.cooperationMode;
      }
      
      // 联系方式 - 使用标准字段名
      if (formData.email.trim()) {
        submitData['contact_email'] = formData.email.trim();
      }
      if (formData.phone.trim()) {
        submitData['contact_phone'] = formData.phone.trim();
      }
      if (formData.telegram.trim()) {
        submitData['contact_telegram'] = formData.telegram.trim();
      }
      if (formData.whatsapp.trim()) {
        submitData['contact_wechat'] = formData.whatsapp.trim();
      }
      
      // 留言
      if (formData.message.trim()) {
        submitData['additional_message'] = formData.message.trim();
      }
      
      // 添加表单类型标识
      submitData['form_type'] = 'creator_application';
      submitData['submission_time'] = new Date().toISOString();
      
      // 隐私政策同意
      submitData['privacy_consent'] = formData.privacyAccepted ? 'agreed' : 'not_agreed';

      // 使用 application/x-www-form-urlencoded 格式提交
      const formBody = new URLSearchParams();
      Object.entries(submitData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://getform.io/f/amdywneb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        // 重置表单
        setFormData({
          name: '',
          country: '',
          platforms: [],
          contentField: '',
          socialLinks: '',
          followers: '',
          cooperationMode: '',
          telegram: '',
          whatsapp: '',
          phone: '',
          email: '',
          message: '',
          privacyAccepted: false
        });
      } else {
        const errorData = await response.json();
        console.error('提交失败:', errorData);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('提交错误:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="apply-form" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('apply_form_title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('apply_form_desc')}
          </p>
          
          {/* 联系方式信息 */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('or_contact_directly')}</h3>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
              <div className="flex items-center text-gray-700">
                <i className="ri-mail-line text-blue-600 text-xl mr-2"></i>
                <span className="font-medium">business@koluno.com</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-telegram-line text-blue-600 text-xl mr-2"></i>
                <span className="font-medium">@koluno2020</span>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-wechat-line text-green-600 text-xl mr-2"></i>
                <span className="font-medium">ljy520155</span>
              </div>
            </div>
          </div>

          {/* ... existing code ... */}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} data-readdy-form id="creator-application">
            {/* 基础信息 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('basic_info')}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label={i18n.language === 'zh' ? '姓名 *' : 'Name *'}
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
                <Input
                  label={i18n.language === 'zh' ? '国家/地区 *' : 'Country/Region *'}
                  name="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  required
                />
                <div className="md:col-span-2">
                  <Select
                    label={i18n.language === 'zh' ? '内容领域 *' : 'Content Field *'}
                    name="contentField"
                    value={formData.contentField}
                    onChange={(e) => handleInputChange('contentField', e.target.value)}
                    options={contentFields}
                    required
                  />
                </div>
              </div>
            </div>

            {/* 平台信息 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('platform_info')}</h3>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {i18n.language === 'zh' ? '主要平台 * (可多选)' : 'Main Platforms * (Multiple selection allowed)'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {platformOptions.map((platform) => (
                    <label key={platform} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="platforms"
                        checked={formData.platforms.includes(platform)}
                        onChange={() => handlePlatformChange(platform)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{platform}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Textarea
                label={i18n.language === 'zh' ? '社媒链接 * (每行一个链接)' : 'Social Media Links * (One link per line)'}
                name="socialLinks"
                value={formData.socialLinks}
                onChange={(e) => handleInputChange('socialLinks', e.target.value)}
                placeholder={i18n.language === 'zh' ? 
                  "https://tiktok.com/@username\nhttps://instagram.com/username\nhttps://youtube.com/@username" :
                  "https://tiktok.com/@username\nhttps://instagram.com/username\nhttps://youtube.com/@username"
                }
                rows={4}
                maxLength={500}
                required
              />

              <div className="mt-6">
                <Input
                  label={i18n.language === 'zh' ? '粉丝数 *' : 'Follower Count *'}
                  name="followers"
                  value={formData.followers}
                  onChange={(e) => handleInputChange('followers', e.target.value)}
                  placeholder={i18n.language === 'zh' ? '例：10K, 50K, 100K' : 'e.g.: 10K, 50K, 100K'}
                  required
                />
              </div>
            </div>

            {/* 合作偏好 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('cooperation_preference')}</h3>
              <div>
                <Select
                  label={i18n.language === 'zh' ? '合作方式偏好' : 'Cooperation Mode Preference'}
                  name="cooperationMode"
                  value={formData.cooperationMode}
                  onChange={(e) => handleInputChange('cooperationMode', e.target.value)}
                  options={cooperationModes}
                />
              </div>
            </div>

            {/* 联系方式 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t('contact_info')} <span className="text-red-500">*</span>
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {i18n.language === 'zh' ? '请至少填写一种联系方式' : 'Please provide at least one contact method'}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Telegram"
                  name="telegram"
                  value={formData.telegram}
                  onChange={(e) => handleInputChange('telegram', e.target.value)}
                  placeholder="@username"
                />
                <Input
                  label={i18n.language === 'zh' ? '微信' : 'WeChat'}
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                  placeholder="@username"
                />
                <Input
                  label={i18n.language === 'zh' ? '电话' : 'Phone'}
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1234567890"
                />
                <Input
                  label={i18n.language === 'zh' ? '邮箱' : 'Email'}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              {contactError && (
                <p className="text-red-500 text-sm mt-2">{contactError}</p>
              )}
            </div>

            {/* 留言 */}
            <div className="mb-8">
              <Textarea
                label={t('message_optional')}
                name="message"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder={i18n.language === 'zh' ? 
                  "告诉我们更多关于您的创作风格、合作期望或其他想法..." :
                  "Tell us more about your creative style, collaboration expectations, or other ideas..."
                }
                rows={4}
                maxLength={500}
              />
            </div>

            {/* 隐私同意 */}
            <div className="mb-8">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="privacyAccepted"
                  checked={formData.privacyAccepted}
                  onChange={(e) => handleInputChange('privacyAccepted', e.target.checked)}
                  className="mt-1 mr-3"
                  required
                />
                <span className="text-sm text-gray-600">
                  {t('privacy_agreement')} <a href="/privacy" className="text-purple-600 hover:underline">{t('privacy_policy')}</a> {i18n.language === 'zh' ? '和' : 'and'} <a href="/terms" className="text-purple-600 hover:underline">{t('terms_of_service')}</a>{t('privacy_agreement_desc')}
                </span>
              </label>
            </div>

            {/* 提交按钮 */}
            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="px-12"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    {t('submitting')}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    {t('submit_application')}
                  </>
                )}
              </Button>
            </div>

            {/* 提交状态反馈 */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <i className="ri-check-circle-line text-green-600 text-xl mr-2"></i>
                <span className="text-green-700">{t('submit_success')}</span>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                <i className="ri-error-warning-line text-red-600 text-xl mr-2"></i>
                <span className="text-red-700">{t('submit_error')}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
